import _ from 'underscore';
import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import hoistNonReactStatics from 'hoist-non-react-statics';

import { getDisplayName } from '@bingads-webui-react/hoc-utils';
import { SuggestionStatus, mapSuggestionResponseToState } from '@bingads-webui-campaign/media-repository';
import { CopilotSuggestionsTracker } from '@bingads-webui-campaign-react/copilot-common';

import { SuggestionsTracker } from './utils';

export const RecommendationContext = React.createContext([
  {},
  _.noop,
  _.noop,
]);

export function RecommendationContextProvider(props) {
  const { children } = props;
  const [recommendationContext, setRecommendationContext] = useState({
    finalUrl: undefined,
    recommendationSource: 'undetermined',
    finalUrls: [],
    themes: [],
    headlines: [],
    longHeadlines: [],
    descriptions: [],
    businessName: undefined,
    images: { ...mapSuggestionResponseToState(SuggestionStatus.EmptyURL) },
    tracking: new SuggestionsTracker(),
    copilotTracking: new CopilotSuggestionsTracker(),
  });
  const updateRecommendationContext = useCallback((delta) => {
    setRecommendationContext(currentContext => ({
      ...currentContext,
      ...delta,
    }));
  }, []);

  const reportRecommendations = useCallback((recommendationsReport) => {
    setRecommendationContext((currentContext) => {
      const {
        finalUrl,
        recommendationSource,
        tracking,
        headlines,
        longHeadlines,
        descriptions,
        businessName,
        images,
      } = currentContext;
      const recommendations = {
        headlines,
        longHeadlines,
        descriptions,
        businessName,
        images,
      };
      if (recommendationSource !== 'copilot') {
        tracking.add(finalUrl, recommendationSource === 'offline', recommendations, recommendationsReport);
      }
      return currentContext;
    });
  }, []);

  return (
    <RecommendationContext.Provider value={[
      recommendationContext,
      updateRecommendationContext,
      reportRecommendations,
    ]}
    >
      {children}
    </RecommendationContext.Provider>
  );
}

RecommendationContextProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.arrayOf(PropTypes.node)]).isRequired,
};

export const withRecommendationContext = (WrappedComponent) => {
  const HoC = props => (
    <RecommendationContextProvider>
      <WrappedComponent {...props} />
    </RecommendationContextProvider>
  );
  HoC.displayName = `WithRecommendationContext(${getDisplayName(WrappedComponent)})`;

  hoistNonReactStatics(HoC, WrappedComponent);

  return HoC;
};
